// The inspector's Page tab ("Page design"), in Flowmark's layout. Groups, in
// order: Look, Colors, Text and style, Logo, Form, Page link, Search and
// sharing (SearchSharing.tsx, also in the Share panel), Tracking codes, then anything the editor adds after. Every value
// and setter comes in through props; nothing here reads or writes the page on
// its own.
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { LandingPage, PageFont, PageFormRef, PageStyle, PageTheme } from '../model/pages';
import { applyArchetype } from '../render/archetypes';
import { CANONICAL_STYLES, STYLE_LABEL, styleLabel } from '../render/styles';
import { FONTS, PAGE_FONTS } from '../render/fonts';
import ImageInput from './ImageInput';
import {
  Block, Choice, Field, INSPECTOR_SCOPE, SelectField, TEXT_BTN, TextField, useBlockOpen,
} from './InspectorKit';
import { ColorField } from './ColorField';
import { LookTiles } from './LookTiles';
import { formStatusLine } from './FormGroup';
import { PageLinkField, SeoFields, type PageSeo, type SlugMessage } from './SearchSharing';
import { PageTracking } from './PageTracking';

export type { PageSeo, SlugMessage };

/** One-click brand colors under the brand color field. */
export const BRAND_SWATCHES: readonly string[] = [
  '#4f46e5', '#2563eb', '#0ea5e9', '#0d9488', '#10b981', '#84cc16',
  '#f59e0b', '#f97316', '#e11d48', '#ec4899', '#8b5cf6', '#E94F37',
];
/** What a page without a brand color shows in the color fields. */
const DEFAULT_BRAND = '#4f46e5';

const FORM_MODES = [['inline', 'Inline'], ['popup', 'Popup']] as const;

export interface PageTabProps {
  page: LandingPage;
  /** page.theme */
  theme: PageTheme;
  setTheme: (t: Partial<PageTheme>) => void;
  /** Patches the page row (the internal name). */
  patch: (p: Partial<LandingPage>) => void;

  /** Page link: called on every keystroke (PageEditor's checkSlug). */
  onSlugChange: (slug: string) => void;
  slugMsg: SlugMessage;
  /** Marks the link field invalid. */
  slugInvalid?: boolean;
  slugTaken?: boolean;
  /** Increment to open the Page link group, scroll to it and focus the link. */
  revealLinkSignal?: number;
  /** Timestamp used to open and focus Analytics and scripts from the numbers panel. */
  revealTrackingSignal?: number;

  /** Search and sharing: the owner's values and what blank fields fall back to. */
  seo: PageSeo;
  seoFallback: { title: string; description: string; image: string; favicon: string };
  setSeo: (p: Partial<PageSeo>) => void;
  /** The host the link preview shows (the app's host, or a chosen custom domain). */
  host?: string;
  /** Every host the page can be shared on (the app's first). Two or more: a
   *  picker for the link preview. */
  hosts?: string[];
  onHostChange?: (host: string) => void;

  /** The embedded form, named in the Form group. */
  selectedForm?: PageFormRef | null;
  /** The page is live (the form's draft note says so). */
  pageLive?: boolean;
  /** "Change": selects the page's Form section. Leave out when there is none. */
  onChangeForm?: () => void;

  /** More groups after Tracking codes. */
  children?: ReactNode;
}

// A reveal that arrives as the tab mounts (the editor switched to it to show
// the link) still runs; a remount later with the same number does not.
const revealMemo = { signal: 0, at: 0 };
const REVEAL_WINDOW_MS = 1000;

function reducedMotion(): boolean {
  try { return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

export function PageTab(props: PageTabProps) {
  const {
    page, theme, setTheme, patch, onSlugChange, slugMsg, slugInvalid, slugTaken, revealLinkSignal, revealTrackingSignal,
    seo, seoFallback, setSeo, host, hosts = [], onHostChange, selectedForm, pageLive = false, onChangeForm, children,
  } = props;

  // ── Page link reveal ────────────────────────────────────────────────
  const [, setLinkOpen] = useBlockOpen('Page link', true);
  const [trackingOpen, setTrackingOpen] = useBlockOpen('Tracking codes', false);
  const trackingRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!revealTrackingSignal || Date.now() - revealTrackingSignal > 1000) return;
    if (!trackingOpen) { setTrackingOpen(true); return; }
    const input = trackingRef.current?.querySelector<HTMLInputElement>('[role="switch"]');
    input?.focus({ preventScroll: true });
    trackingRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' });
  }, [revealTrackingSignal, trackingOpen, setTrackingOpen]);
  const linkRef = useRef<HTMLInputElement>(null);
  const pendingReveal = useRef(false);
  const focusLink = useCallback((el: HTMLInputElement) => {
    el.scrollIntoView?.({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
    el.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    const s = revealLinkSignal ?? 0;
    if (!s) return;
    const now = Date.now();
    if (s === revealMemo.signal && now - revealMemo.at > REVEAL_WINDOW_MS) return;
    if (s !== revealMemo.signal) { revealMemo.signal = s; revealMemo.at = now; }
    if (linkRef.current) { focusLink(linkRef.current); return; }
    pendingReveal.current = true;
    setLinkOpen(true);
  }, [revealLinkSignal, focusLink, setLinkOpen]);
  useEffect(() => {
    if (!pendingReveal.current || !linkRef.current) return;
    pendingReveal.current = false;
    focusLink(linkRef.current);
  });

  const linkBad = !!(slugInvalid || slugTaken);
  const formBgOpacity = theme.form_bg_opacity ?? 100;
  const opacityText = formBgOpacity === 0 ? 'transparent' : formBgOpacity === 100 ? 'solid' : `${formBgOpacity}% glass`;
  const styleKnown = !!theme.style && (CANONICAL_STYLES as readonly string[]).includes(theme.style);
  const shareHost = host || (typeof window !== 'undefined' ? window.location.host : '');
  const formStatus = formStatusLine(selectedForm, pageLive);

  return (
    <div className={`min-w-0 pb-8 ${INSPECTOR_SCOPE}`} data-inspector="page">
      <div className="flex min-w-0 items-center px-4 pb-3 pt-3.5">
        <h2 className="text-base font-semibold tracking-[-0.01em] text-text">Page design</h2>
      </div>

      <Block name="Look" collapsible={false}>
        <LookTiles value={theme.archetype} onPick={(key) => setTheme(applyArchetype(theme, key))} />
      </Block>

      <Block name="Colors" defaultOpen>
        <ColorField label="Brand color" value={theme.brand_color} fallback={DEFAULT_BRAND} onChange={(c) => setTheme({ brand_color: c })}>
          <BrandSwatches value={theme.brand_color} onPick={(c) => setTheme({ brand_color: c })} />
        </ColorField>
        <ColorField label="Button color" value={theme.button_color ?? theme.brand_color} fallback={DEFAULT_BRAND}
          onChange={(c) => setTheme({ button_color: c })}
          aside={theme.button_color
            ? <button type="button" className={TEXT_BTN} onClick={() => setTheme({ button_color: undefined })}>Same as brand</button>
            : undefined} />
        <ColorField label="Form background" value={theme.form_bg_color} fallback="#ffffff" onChange={(c) => setTheme({ form_bg_color: c })}
          aside={<span className="shrink-0 text-xs tnum text-textDim" data-opacity-label>{opacityText}</span>}>
          <input type="range" min={0} max={100} value={formBgOpacity} aria-label="Form background opacity"
            className="h-[18px] w-full min-w-0 cursor-pointer accent-text"
            onChange={(e) => setTheme({ form_bg_opacity: Number(e.target.value) })} />
        </ColorField>
      </Block>

      <Block name="Text and style">
        <SelectField label="Font" value={theme.font ?? ''} onChange={(v) => setTheme({ font: v ? (v as PageFont) : undefined })}>
          <option value="">Default</option>
          {PAGE_FONTS.map((f) => <option key={f} value={f}>{FONTS[f].label}</option>)}
        </SelectField>
        <SelectField label="Fine-tune style" value={theme.style ?? ''} onChange={(v) => { if (v) setTheme({ style: v as PageStyle }); }}>
          {!styleKnown && <option value={theme.style ?? ''}>{styleLabel(theme.style)}</option>}
          {CANONICAL_STYLES.map((st) => <option key={st} value={st}>{STYLE_LABEL[st]}</option>)}
        </SelectField>
      </Block>

      <Block name="Logo" collapsible={false}>
        <div role="group" aria-label="Logo" className="min-w-0">
          <ImageInput value={theme.logo_url ?? ''} onChange={(url) => setTheme({ logo_url: url })} placeholder="Logo URL" />
        </div>
      </Block>

      <Block name="Page form" title="Form">
        <Field label="Embedded form" hint={formStatus || undefined}
          aside={onChangeForm ? <button type="button" className={TEXT_BTN} onClick={onChangeForm}>Change</button> : undefined}>
          <p className="min-w-0 truncate text-sm text-text">{selectedForm ? selectedForm.name : 'None yet'}</p>
        </Field>
        <Choice label="Form display" value={theme.form_mode ?? 'inline'} options={FORM_MODES} onChange={(m) => setTheme({ form_mode: m })} />
      </Block>

      <Block name="Page link" defaultOpen>
        <TextField label="Internal name" value={page.name} onChange={(v) => patch({ name: v })} hint="Shows in your Pages list, and as the browser title when Search and sharing has none." />
        <PageLinkField slug={page.slug} onChange={onSlugChange} msg={slugMsg} bad={linkBad} inputRef={linkRef} />
      </Block>

      <Block name="Search and sharing">
        <SeoFields seo={seo} fallback={seoFallback} setSeo={setSeo} host={shareHost} hosts={hosts} onHostChange={onHostChange} />
      </Block>

      <div ref={trackingRef}>
        <Block name="Tracking codes" title="Analytics and scripts">
          <PageTracking theme={theme} setTheme={setTheme} host={shareHost} />
        </Block>
      </div>

      {children}
    </div>
  );
}

/** The twelve one-click brand colors: one row (two on touch screens), the
 *  chosen one ringed in ink. */
function BrandSwatches({ value, onPick }: { value: string | undefined; onPick: (c: string) => void }) {
  const cur = (value ?? '').toLowerCase();
  return (
    <div role="group" aria-label="Brand color presets"
      className="grid min-w-0 grid-cols-12 gap-1.5 [@media(pointer:coarse)]:grid-cols-6 [@media(pointer:coarse)]:gap-2.5">
      {BRAND_SWATCHES.map((c) => {
        const on = cur === c.toLowerCase();
        return (
          <button key={c} type="button" aria-label={`Use ${c}`} title={c} aria-pressed={on} onClick={() => onPick(c)}
            className={`aspect-square w-full max-w-8 justify-self-center rounded-full shadow-[inset_0_0_0_1px_var(--pe-hairline)] transition-transform duration-100 hover:scale-110 motion-reduce:hover:scale-100 ${on ? 'ring-2 ring-text ring-offset-2 ring-offset-surface' : ''}`}
            style={{ background: c }} />
        );
      })}
    </div>
  );
}

